import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaLandmark } from 'react-icons/fa';
import PopupDialog from '../../common/PopupDialog/PopupDialog';
import { BankTransaction } from '../../../models/BankTransaction';
import { Ledger } from '../../../models/Ledger';
import { FiscalYear } from '../../../models/FiscalYear';
import { VatReturn } from '../../../models/VatReturn';
import { VatReturnRestDataProvider } from '../../../data/VatReturnRestDataProvider';
import { BankTransactionRestDataProvider } from '../../../data/BankTransactionRestDataProvider';

interface Props {
  transaction: BankTransaction;
  ledgers: Ledger[];
  fiscalYears: FiscalYear[];
  onClose: () => void;
  onBooked: (updatedTx: BankTransaction) => void;
}

/** Ledger code for "Betalen / Ontvangen BTW" (1830) — the VAT settlement account. */
const BTW_SETTLEMENT_CODE = '1830';
/** Ledger code for "BTW Betalingsverschil" (1542) — P&L account for small bank-payment rounding differences (≤ €5). */
const BTW_DIFFERENCE_CODE = '1542';
/** Ledger code for "BTW Correctie Verschil" (1543) — balance-sheet clearing account for large BTW differences requiring Belastingdienst correction (> €5). */
const BTW_LARGE_DIFFERENCE_CODE = '1543';
/** Ledger code for "Belastingdienst Boete / Dwangsom" (4980) — non-deductible fine / penalty expense account. */
const BTW_BOETE_CODE = '4980';
/** Maximum absolute difference (€) that is auto-split to the small-difference ledger. Anything above this goes to the large-difference ledger. */
const MAX_AUTO_DIFF = 5;

const BankTransactionVatPaymentModal: React.FC<Props> = ({
  transaction,
  ledgers,
  fiscalYears,
  onClose,
  onBooked,
}) => {
  const { t } = useTranslation(['common', 'screens']);

  const vatReturnProvider = useMemo(() => new VatReturnRestDataProvider(), []);
  const txProvider        = useMemo(() => new BankTransactionRestDataProvider(), []);

  const [vatReturns, setVatReturns]         = useState<VatReturn[]>([]);
  const [loading, setLoading]               = useState(true);
  const [selectedId, setSelectedId]         = useState<string>('');
  const [saving, setSaving]                 = useState(false);
  const [confirmOpen, setConfirmOpen]       = useState(false);
  /** Which ledger the large-diff line is routed to — defaults to BTW Correctie Verschil (1543). */
  const [largeDiffLedgerCode, setLargeDiffLedgerCode] = useState<string>(BTW_LARGE_DIFFERENCE_CODE);

  // Load all booked VAT returns once on mount
  useEffect(() => {
    vatReturnProvider.getAll()
      .then(all => setVatReturns(all.filter(v => v.isBooked)))
      .finally(() => setLoading(false));
  }, [vatReturnProvider]);

  /** The "Betalen / Ontvangen BTW" (1830) ledger used as the contra-account. */
  const btwSettlementLedger = useMemo(
    () => ledgers.find(l => l.code === BTW_SETTLEMENT_CODE),
    [ledgers],
  );

  /** The "BTW Betalingsverschil" (1542) ledger used to absorb small rounding differences. */
  const btwDifferenceLedger = useMemo(
    () => ledgers.find(l => l.code === BTW_DIFFERENCE_CODE),
    [ledgers],
  );

  /** The "BTW Correctie Verschil" (1543) ledger used to park large BTW discrepancies pending Belastingdienst correction. */
  const btwLargeDifferenceLedger = useMemo(
    () => ledgers.find(l => l.code === BTW_LARGE_DIFFERENCE_CODE),
    [ledgers],
  );

  /** The "Belastingdienst Boete / Dwangsom" (4980) ledger for non-deductible fines and penalties. */
  const btwBoeteLedger = useMemo(
    () => ledgers.find(l => l.code === BTW_BOETE_CODE),
    [ledgers],
  );

  /** The currently selected ledger for the large-diff split line. */
  const activeLargeDiffLedger = useMemo(() => {
    if (largeDiffLedgerCode === BTW_BOETE_CODE) return btwBoeteLedger;
    return btwLargeDifferenceLedger;
  }, [largeDiffLedgerCode, btwBoeteLedger, btwLargeDifferenceLedger]);

  const selectedReturn = useMemo(
    () => vatReturns.find(v => v.id === selectedId) ?? null,
    [vatReturns, selectedId],
  );

  const fiscalYearName = (fyId: string | null) =>
    fiscalYears.find(fy => fy.id === fyId)?.name ?? '—';

  /** Booked VAT returns sorted ascending by fiscal year start date, then by quarter. */
  const sortedVatReturns = useMemo(() => {
    const fyStartMap = new Map(fiscalYears.map(fy => [fy.id, fy.start]));
    return [...vatReturns].sort((a, b) => {
      const aStart = fyStartMap.get(a.fiscalYearId ?? '') ?? '';
      const bStart = fyStartMap.get(b.fiscalYearId ?? '') ?? '';
      if (aStart !== bStart) return aStart.localeCompare(bStart);
      return a.quarter.localeCompare(b.quarter);
    });
  }, [vatReturns, fiscalYears]);

  /** Determine the fiscal year: prefer the one attached to the selected VAT return,
   *  fall back to matching the transaction's value date. */
  const autoFiscalYear = useMemo(() => {
    if (selectedReturn?.fiscalYearId) {
      return fiscalYears.find(fy => fy.id === selectedReturn.fiscalYearId) ?? null;
    }
    if (!fiscalYears.length) return null;
    const txDate = new Date(transaction.valueDate);
    return (
      fiscalYears.find(fy => txDate >= new Date(fy.start) && txDate <= new Date(fy.end))
      ?? fiscalYears[0]
    );
  }, [selectedReturn, fiscalYears, transaction.valueDate]);

  const isDebitTx = transaction.creditDebitIndicator === 'D' || transaction.creditDebitIndicator === 'RC';

  // Amount difference between transaction and selected VAT return
  const vatReturnAmount = selectedReturn ? Math.abs(selectedReturn.totalVatPayable) : 0;
  const diff            = selectedReturn ? transaction.amount - vatReturnAmount : 0;
  const absDiff         = Math.abs(diff);
  /** Difference is small enough to auto-split to the BTW difference ledger. */
  const hasSmallDiff = selectedReturn !== null && absDiff > 0.005 && absDiff <= MAX_AUTO_DIFF;
  /** Difference is large enough to require a correction with the Belastingdienst; split to 1543. */
  const hasLargeDiff = selectedReturn !== null && absDiff > MAX_AUTO_DIFF;

  const canBook = !!selectedReturn && !!btwSettlementLedger && !!autoFiscalYear && !saving
    && (!hasSmallDiff || !!btwDifferenceLedger)
    && (!hasLargeDiff || !!activeLargeDiffLedger);

  const handleConfirm = async () => {
    if (!selectedReturn || !btwSettlementLedger || !autoFiscalYear) return;
    setConfirmOpen(false);
    setSaving(true);
    try {
      const fyName  = fiscalYearName(selectedReturn.fiscalYearId);
      const baseDesc = `${t('screens:bankTransactions.vatPayment.ledgerDescription')} ${selectedReturn.quarter} ${fyName}`;

      const lines: { ledgerId: string; description: string; amount: number }[] = [];
      const diffDesc = `${t('screens:bankTransactions.vatPayment.differenceDescription')} ${selectedReturn.quarter} ${fyName}`;

      if (hasSmallDiff && btwDifferenceLedger) {
        // Split: exact VAT return amount → 1830, small rounding remainder → 1542
        lines.push({ ledgerId: btwSettlementLedger.id, description: baseDesc, amount: vatReturnAmount });
        lines.push({
          ledgerId:    btwDifferenceLedger.id,
          description: diffDesc,
          amount:      diff, // can be negative; sum of lines always equals transaction.amount
        });
      } else if (hasLargeDiff && activeLargeDiffLedger) {
        // Split: exact VAT return amount → 1830, large difference → selected ledger (1543 or 4980)
        lines.push({ ledgerId: btwSettlementLedger.id, description: baseDesc, amount: vatReturnAmount });
        lines.push({
          ledgerId:    activeLargeDiffLedger.id,
          description: diffDesc,
          amount:      diff, // can be negative; sum of lines always equals transaction.amount
        });
      } else {
        // No split: book the full transaction amount to 1830 (exact match)
        lines.push({ ledgerId: btwSettlementLedger.id, description: baseDesc, amount: transaction.amount });
      }

      const result = await txProvider.book(transaction.id, {
        fiscalYearId: autoFiscalYear.id,
        lines,
        isVatPayment: true,
      });
      const updatedTx: BankTransaction = {
        ...transaction,
        ledgerReferenceCode: result.referenceNumber,
        isVatPaymentBooked:  true,
      };
      onBooked(updatedTx);
    } catch {
      // Errors surfaced via BaseRestDataProvider toast
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PopupDialog
        open
        title={t('screens:bankTransactions.vatPayment.title')}
        onClose={saving ? () => {} : onClose}
        actions={
          <>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!canBook}
              onClick={() => setConfirmOpen(true)}
            >
              <FaLandmark style={{ marginRight: 6 }} />
              {saving
                ? t('screens:bankTransactions.vatPayment.booking')
                : t('screens:bankTransactions.vatPayment.book')}
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              disabled={saving}
              onClick={onClose}
            >
              {t('common:cancel')}
            </button>
          </>
        }
      >
        {/* ── Transaction summary ── */}
        <div className="bank-booking-summary mb-3">
          <div className="bank-booking-summary-row">
            <span className="bank-booking-summary-label">{t('screens:bankTransactions.booking.transactionDate')}</span>
            <span className="bank-booking-summary-value">{transaction.valueDate}</span>
          </div>
          <div className="bank-booking-summary-row">
            <span className="bank-booking-summary-label">{t('screens:bankTransactions.booking.counterparty')}</span>
            <span className="bank-booking-summary-value">
              {transaction.narrativeCounterpartyName || transaction.customerReference || '—'}
            </span>
          </div>
          <div className="bank-booking-summary-row">
            <span className="bank-booking-summary-label">{t('screens:bankTransactions.booking.amount')}</span>
            <span className={`bank-booking-summary-value ${isDebitTx ? 'bank-transactions-amount--negative' : 'bank-transactions-amount--positive'}`}>
              {isDebitTx ? '-' : '+'}{transaction.amount.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              {' '}{transaction.currency}
            </span>
          </div>
          {(transaction.narrativeRemittanceInfo || transaction.narrative) && (
            <div className="bank-booking-summary-row">
              <span className="bank-booking-summary-label">{t('screens:bankTransactions.booking.description')}</span>
              <span className="bank-booking-summary-value">
                {transaction.narrativeRemittanceInfo || transaction.narrative}
              </span>
            </div>
          )}
        </div>

        {/* ── Missing ledger warning ── */}
        {!btwSettlementLedger && (
          <div className="alert alert-warning small mb-3">
            {t('screens:bankTransactions.vatPayment.noLedger', { code: BTW_SETTLEMENT_CODE })}
          </div>
        )}

        {/* ── VAT Return selector ── */}
        <div className="mb-2">
          <label className="form-label fw-semibold">
            {t('screens:bankTransactions.vatPayment.selectReturn')}
          </label>
        </div>

        {loading ? (
          <div className="text-muted small mb-3">{t('common:loading')}</div>
        ) : vatReturns.length === 0 ? (
          <div className="text-muted small mb-3">{t('screens:bankTransactions.vatPayment.noReturns')}</div>
        ) : (
          <div className="bank-payment-invoice-list mb-3">
            <table className="table table-sm table-hover mb-0">
              <thead>
                <tr>
                  <th style={{ width: 36 }} />
                  <th>{t('screens:bankTransactions.vatPayment.quarter')}</th>
                  <th>{t('screens:bankTransactions.vatPayment.fiscalYear')}</th>
                  <th className="text-end">{t('screens:bankTransactions.vatPayment.vatPayable')}</th>
                  <th>{t('screens:bankTransactions.vatPayment.direction')}</th>
                </tr>
              </thead>
              <tbody>
                {sortedVatReturns.map(vr => {
                  const isSelected = selectedId === vr.id;
                  const isPayment  = vr.totalVatPayable > 0;
                  return (
                    <tr
                      key={vr.id}
                      className={isSelected ? 'bank-payment-invoice-row--selected' : ''}
                      onClick={() => !saving && setSelectedId(prev => prev === vr.id ? '' : vr.id)}
                      style={{ cursor: saving ? 'default' : 'pointer' }}
                    >
                      <td>
                        <input
                          type="radio"
                          className="form-check-input"
                          checked={isSelected}
                          readOnly
                          disabled={saving}
                          onClick={e => e.stopPropagation()}
                        />
                      </td>
                      <td className="fw-semibold">{vr.quarter}</td>
                      <td>{fiscalYearName(vr.fiscalYearId)}</td>
                      <td className={`text-end font-monospace ${isPayment ? 'bank-transactions-amount--negative' : 'bank-transactions-amount--positive'}`}>
                        {Math.abs(vr.totalVatPayable).toLocaleString('nl-NL', {
                          minimumFractionDigits:  2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td>
                        <span className={`badge ${isPayment ? 'bg-danger' : 'bg-success'}`}>
                          {isPayment
                            ? t('screens:bankTransactions.vatPayment.directionPayment')
                            : t('screens:bankTransactions.vatPayment.directionRefund')}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Large-difference type selector ── */}
        {hasLargeDiff && selectedReturn && (
          <div className="mb-2">
            <label className="form-label small fw-semibold mb-1">
              {t('screens:bankTransactions.vatPayment.largeDiffLedgerLabel')}
            </label>
            <select
              className="form-select form-select-sm"
              value={largeDiffLedgerCode}
              onChange={e => setLargeDiffLedgerCode(e.target.value)}
              disabled={saving}
            >
              <option value={BTW_LARGE_DIFFERENCE_CODE}>
                {t('screens:bankTransactions.vatPayment.largeDiffLedgerBtwCorrectie')}
              </option>
              <option value={BTW_BOETE_CODE}>
                {t('screens:bankTransactions.vatPayment.largeDiffLedgerBoete')}
              </option>
            </select>
          </div>
        )}

        {/* ── Amount difference notices ── */}
        {hasSmallDiff && btwDifferenceLedger && selectedReturn && (
          <div className="alert alert-info small mb-0">
            {t('screens:bankTransactions.vatPayment.smallDiffInfo', {
              vatAmount: vatReturnAmount.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
              diff: Math.abs(diff).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
              diffDirection: diff > 0
                ? t('screens:bankTransactions.vatPayment.diffOver')
                : t('screens:bankTransactions.vatPayment.diffUnder'),
            })}
          </div>
        )}
        {hasSmallDiff && !btwDifferenceLedger && (
          <div className="alert alert-warning small mb-0">
            {t('screens:bankTransactions.vatPayment.noDifferenceLedger', { code: BTW_DIFFERENCE_CODE })}
          </div>
        )}
        {hasLargeDiff && activeLargeDiffLedger && selectedReturn && (
          <div className={`alert ${largeDiffLedgerCode === BTW_BOETE_CODE ? 'alert-danger' : 'alert-warning'} small mb-0`}>
            {largeDiffLedgerCode === BTW_BOETE_CODE
              ? t('screens:bankTransactions.vatPayment.largeDiffInfoBoete', {
                  vatAmount: vatReturnAmount.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
                  diff: Math.abs(diff).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
                  diffDirection: diff > 0
                    ? t('screens:bankTransactions.vatPayment.diffOver')
                    : t('screens:bankTransactions.vatPayment.diffUnder'),
                })
              : t('screens:bankTransactions.vatPayment.largeDiffInfoBtwCorrectie', {
                  vatAmount: vatReturnAmount.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
                  diff: Math.abs(diff).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
                  diffDirection: diff > 0
                    ? t('screens:bankTransactions.vatPayment.diffOver')
                    : t('screens:bankTransactions.vatPayment.diffUnder'),
                })}
            {largeDiffLedgerCode === BTW_BOETE_CODE && (
              <div className="mt-1 fw-semibold">
                {t('screens:bankTransactions.vatPayment.boeteNote')}
              </div>
            )}
          </div>
        )}
        {hasLargeDiff && !activeLargeDiffLedger && (
          <div className="alert alert-danger small mb-0">
            {t('screens:bankTransactions.vatPayment.noLargeDifferenceLedger', { code: largeDiffLedgerCode })}
          </div>
        )}
      </PopupDialog>

      {/* ── Confirmation dialog ── */}
      {confirmOpen && selectedReturn && (
        <PopupDialog
          open
          title={t('screens:bankTransactions.vatPayment.confirmTitle')}
          onClose={() => setConfirmOpen(false)}
          actions={
            <>
              <button type="button" className="btn btn-primary me-2" onClick={handleConfirm}>
                <FaLandmark style={{ marginRight: 6 }} />
                {t('screens:bankTransactions.vatPayment.confirmBook')}
              </button>
              <button type="button" className="btn btn-outline-secondary" onClick={() => setConfirmOpen(false)}>
                {t('common:cancel')}
              </button>
            </>
          }
        >
        <p>
            {hasSmallDiff && btwDifferenceLedger
              ? t('screens:bankTransactions.vatPayment.confirmMessageWithDiff', {
                  quarter:    selectedReturn.quarter,
                  fiscalYear: fiscalYearName(selectedReturn.fiscalYearId),
                  vatAmount:  vatReturnAmount.toLocaleString('nl-NL', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }),
                  diff: Math.abs(diff).toLocaleString('nl-NL', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }),
                  diffDirection: diff > 0
                    ? t('screens:bankTransactions.vatPayment.diffOver')
                    : t('screens:bankTransactions.vatPayment.diffUnder'),
                })
              : hasLargeDiff && activeLargeDiffLedger && largeDiffLedgerCode === BTW_BOETE_CODE
              ? t('screens:bankTransactions.vatPayment.confirmMessageWithBoete', {
                  quarter:    selectedReturn.quarter,
                  fiscalYear: fiscalYearName(selectedReturn.fiscalYearId),
                  vatAmount:  vatReturnAmount.toLocaleString('nl-NL', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }),
                  diff: Math.abs(diff).toLocaleString('nl-NL', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }),
                  diffDirection: diff > 0
                    ? t('screens:bankTransactions.vatPayment.diffOver')
                    : t('screens:bankTransactions.vatPayment.diffUnder'),
                })
              : hasLargeDiff && activeLargeDiffLedger
              ? t('screens:bankTransactions.vatPayment.confirmMessageWithLargeDiff', {
                  quarter:    selectedReturn.quarter,
                  fiscalYear: fiscalYearName(selectedReturn.fiscalYearId),
                  vatAmount:  vatReturnAmount.toLocaleString('nl-NL', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }),
                  diff: Math.abs(diff).toLocaleString('nl-NL', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }),
                  diffDirection: diff > 0
                    ? t('screens:bankTransactions.vatPayment.diffOver')
                    : t('screens:bankTransactions.vatPayment.diffUnder'),
                })
              : t('screens:bankTransactions.vatPayment.confirmMessage', {
                  quarter:    selectedReturn.quarter,
                  fiscalYear: fiscalYearName(selectedReturn.fiscalYearId),
                  amount:     transaction.amount.toLocaleString('nl-NL', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }),
                })}
          </p>
        </PopupDialog>
      )}
    </>
  );
};

export default BankTransactionVatPaymentModal;

